import { useState } from "react";

import type { LocationFilter } from "@/types/driver";

export function useLocationFilterForm({
  location,
  setLocation,
  clearLocation,
}: {
  location: LocationFilter | null;
  setLocation: (v: LocationFilter) => void;
  clearLocation: () => void;
}) {
  const [searchType, setSearchType] = useState<"address" | "coordinates">("address");
  const [addressLabel, setAddressLabel] = useState<string | null>(null);
  const [resetForm, setResetForm] = useState(false);
  const [latitude, setLatitude] = useState<string>(location?.latitude?.toString() ?? "");
  const [longitude, setLongitude] = useState<string>(location?.longitude?.toString() ?? "");
  const [range, setRange] = useState(location?.radius ?? 50);
  const [distanceUnit, setDistanceUnit] = useState<"miles" | "km">(location?.unit ?? "miles");

  const latNum = Number(latitude);
  const lonNum = Number(longitude);
  const isValid = latitude !== "" && longitude !== "" && !Number.isNaN(latNum) && !Number.isNaN(lonNum);

  const apply = () => {
    if (!isValid) return;
    setLocation({ latitude: latNum, longitude: lonNum, radius: range, unit: distanceUnit });
  };

  const reset = () => {
    setSearchType("address");
    setAddressLabel(null);
    setResetForm(true);
    setTimeout(() => setResetForm(false), 200);
    setLatitude("");
    setLongitude("");
    setDistanceUnit("miles");
    setRange(50);
    clearLocation();
  };

  const handlePlaceSelected = (
    value: {
      location?: { latitude?: number; longitude?: number };
      placePrediction?: { text?: { text?: string } };
    } | null,
  ) => {
    if (value?.location?.latitude != null && value?.location?.longitude != null) {
      setLatitude(String(value.location.latitude));
      setLongitude(String(value.location.longitude));
      setAddressLabel(value.placePrediction?.text?.text ?? null);
    }
  };

  const canClear = !!(location || latitude || longitude);

  return {
    searchType,
    setSearchType,
    addressLabel,
    resetForm,
    latitude,
    setLatitude,
    longitude,
    setLongitude,
    range,
    setRange,
    distanceUnit,
    setDistanceUnit,
    latNum,
    lonNum,
    isValid,
    apply,
    reset,
    handlePlaceSelected,
    canClear,
  };
}
